import { RoleEnum, VerificationStatusEnum } from "../modules/shared/model";

export interface VerificationStep {
    key: string;
    title: string;
    status: "verified" | "pending" | "rejected";
    isRequired: boolean;
}

export interface VerificationData {
    identityStatus?: { id: number };
    businessStatus?: { id: number };
    addressProofStatus?: { id: number };
    identityFiles?: any[];
    businessFiles?: any[];
    addressProofFiles?: any[];
}

export interface User {
    role: { id: number };
    isPhoneVerified?: boolean;
    phone?: string;
}

export interface Company {
    name?: string;
}

export interface VerificationResult {
    progress: number;
    isFullyVerified: boolean;
    isVerified: boolean;
    missingSteps: VerificationStep[];
    requiredSteps: VerificationStep[];
    canPurchase: boolean;
    verificationSteps: VerificationStep[];
}

export class VerificationService {
    /**
     * Get verification steps based on user role and company data
     */
    static getVerificationSteps(
        user: User,
        verificationData: VerificationData | null,
        company: Company | null
    ): VerificationStep[] {
        const steps: VerificationStep[] = [
            {
                key: "identity",
                title: "Identity Verification",
                status: this.getVerificationStatus(verificationData?.identityStatus?.id),
                isRequired: true,
            },
            {
                key: "addressProof",
                title: "Proof of Address",
                status: this.getVerificationStatus(verificationData?.addressProofStatus?.id),
                isRequired: true,
            },
            {
                key: "email",
                title: "Email Verification",
                status: "verified", // Email is always considered verified
                isRequired: true,
            },
        ];

        // Add business registration for professionals with company
        if (user?.role?.id !== RoleEnum.client && company?.name) {
            steps.push({
                key: "business",
                title: "Business Registration",
                status: this.getVerificationStatus(verificationData?.businessStatus?.id),
                isRequired: true,
            });
        }

        // Add phone verification for professional roles (4, 5, 6)
        if (Number(user?.role?.id) >= 4 && Number(user?.role?.id) <= 6) {
            steps.push({
                key: "phone",
                title: "Phone Number Verification",
                status: user?.isPhoneVerified ? "verified" : "pending",
                isRequired: true,
            });
        }

        return steps;
    }

    /**
     * Calculate verification progress percentage
     */
    static calculateVerificationProgress(
        user: User,
        verificationData: VerificationData | null,
        company: Company | null
    ): number {
        const steps = this.getVerificationSteps(user, verificationData, company);
        
        if (steps.length === 0) return 0;

        const verifiedSteps = steps.filter(step => step.status === "verified").length;
        return Math.round((verifiedSteps / steps.length) * 100);
    }

    /**
     * Check if user is fully verified
     */
    static isFullyVerified(
        user: User,
        verificationData: VerificationData | null,
        company: Company | null
    ): boolean {
        const steps = this.getVerificationSteps(user, verificationData, company);
        return steps.every(step => step.status === "verified");
    }

    /**
     * Get missing verification steps
     */
    static getMissingSteps(
        user: User,
        verificationData: VerificationData | null,
        company: Company | null
    ): VerificationStep[] {
        const steps = this.getVerificationSteps(user, verificationData, company);
        return steps.filter(step => step.status !== "verified");
    }

    /**
     * Check if user is verified based on specific criteria
     */
    static isVerified(
        user: User,
        verificationData: VerificationData | null,
        company: Company | null
    ): boolean {
        // Check if progress is 100%
        const progress = this.calculateVerificationProgress(user, verificationData, company);
        if (progress !== 100) {
            return false;
        }

        // Check required verification statuses
        const hasAddressProof = Number(verificationData?.addressProofStatus?.id) === 2;
        const hasIdentity = Number(verificationData?.identityStatus?.id) === 2;

        if (!hasAddressProof || !hasIdentity) {
            return false;
        }

        // If user has a company, check business status
        // if (Number(user.role.id) !== RoleEnum.client && company?.name) {
        //     const hasBusiness = Number(verificationData?.businessStatus?.id) === 2;
        //     if (!hasBusiness) {
        //         return false;
        //     }
        // }

        return true;
    }

    /**
     * Check if user can purchase quotes/place orders
     */
    static canPurchase(
        user: User,
        verificationData: VerificationData | null,
        company: Company | null
    ): boolean {
        // For now, we'll require full verification for purchasing
        // You can modify this logic based on your business requirements
        return this.isFullyVerified(user, verificationData, company);
    }

    /**
     * Get comprehensive verification result
     */
    static getVerificationResult(
        user: User,
        verificationData: VerificationData | null,
        company: Company | null
    ): VerificationResult {
        const steps = this.getVerificationSteps(user, verificationData, company);
        const missingSteps = this.getMissingSteps(user, verificationData, company);
        const progress = this.calculateVerificationProgress(user, verificationData, company);
        const isFullyVerified = this.isFullyVerified(user, verificationData, company);
        const isVerified = this.isVerified(user, verificationData, company);
        const canPurchase = this.canPurchase(user, verificationData, company);

        return {
            progress,
            isFullyVerified,
            isVerified,
            missingSteps,
            requiredSteps: steps.filter(step => step.isRequired),
            canPurchase,
            verificationSteps: steps,
        };
    }

    /**
     * Show verification dialog and redirect to verify page
     */
    static showVerificationDialog(
        missingSteps: VerificationStep[],
        navigate: (path: string) => void,
        showToast: (type: string, title: string, message: string) => void
    ): void {
        const stepNames = missingSteps.map(step => step.title).join(", ");
        
        showToast(
            "warning",
            "Verification Required",
            `Please complete the following verification steps: ${stepNames}. Click to verify your account.`
        );

        // Navigate to verification page
        navigate("/verify");
    }

    /**
     * Handle purchase restriction with verification check
     */
    static handlePurchaseRestriction(
        user: User,
        verificationData: VerificationData | null,
        company: Company | null,
        navigate: (path: string) => void,
        showToast: (type: string, title: string, message: string) => void
    ): boolean {
        const result = this.getVerificationResult(user, verificationData, company);

        if (!result.canPurchase) {
            this.showVerificationDialog(result.missingSteps, navigate, showToast);
            return false;
        }

        return true;
    }

    /**
     * Convert verification status ID to string status
     */
    private static getVerificationStatus(statusId?: number): "verified" | "pending" | "rejected" {
        if (!statusId) return "pending";
        
        if (Number(statusId) === VerificationStatusEnum.accepted) {
            return "verified";
        } else if (Number(statusId) === VerificationStatusEnum.rejected) {
            return "rejected";
        } else {
            return "pending";
        }
    }
}

export default VerificationService;
